import {
  createAssemblyPutEntry,
  createEquipmentPutEntry,
  createJobPutEntry,
  createSubAssemblyPutEntry,
} from 'features/equipment/utils/inventoryManagementHelpers'
import { useRolodexEdit } from 'features/fde'
import { useGeckoNotifications } from 'features/notifications'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import { useCallback, useMemo } from 'react'
import {
  AssemblyConfigurationEntry,
  AssemblyEntry,
  EquipmentConfigurationEntry,
  EquipmentEntry,
  GeckoAssemblyJSON,
  GeckoEquipmentJSON,
  GeckoSubAssemblyJSON,
  InventoryEntryType,
  JobEntry,
  RolodexEntryPutJson,
  RolodexEntryType,
  SubAssemblyEntry,
} from 'types'

const PARENT_SYNC_FIELDS = [
  'status',
  'intent',
  'dept_owner',
  'owner',
  'home_location',
] as const

type ParentSyncFieldKey = (typeof PARENT_SYNC_FIELDS)[number]

const isParentSyncField = (key: string): key is ParentSyncFieldKey => {
  return PARENT_SYNC_FIELDS.includes(key as ParentSyncFieldKey)
}

interface UseUpdateAllEquipmentTreeForScannerProps {
  equipmentEntries?: EquipmentEntry[] | undefined
  assemblyEntries?: AssemblyEntry[] | undefined
  activeEquipmentConfigurationEntries?:
    | EquipmentConfigurationEntry[]
    | undefined
  activeAssemblyConfigurationEntries?: AssemblyConfigurationEntry[] | undefined
  refreshEquipmentTable?: () => void
  refreshAssemblyTable?: () => void
  refreshSubAssemblyTable?: () => void
  refreshEquipmentData?: () => void
  refreshAssemblyData?: () => void
  refreshSubAssemblyData?: () => void
  refreshAuditLog?: () => void
}

/**
 * Hook for updating inventory trees:
 * - Equipment tree: equipment → assemblies → sub-assemblies
 * - Assembly tree: assemblies → sub-assemblies
 *
 * Kept the job entry as an optional param here. For backwards compatibility with the old
 * scanner hook code. Should effectively update all the sub entries when a change is made in inventory.
 */
export const useUpdateAllEquipmentTreeForScanner = ({
  equipmentEntries,
  assemblyEntries,
  activeEquipmentConfigurationEntries,
  activeAssemblyConfigurationEntries,
  refreshEquipmentTable,
  refreshAssemblyTable,
  refreshSubAssemblyTable,
  refreshEquipmentData,
  refreshAssemblyData,
  refreshSubAssemblyData,
  refreshAuditLog,
}: UseUpdateAllEquipmentTreeForScannerProps) => {
  const { rolodexPut } = useRolodexEdit()
  const { successNotification, errorNotification } = useGeckoNotifications()

  const relevantEquipmentConfigEntries = useMemo(
    () =>
      activeEquipmentConfigurationEntries?.filter((configEntry) =>
        equipmentEntries?.some(
          (equipment) =>
            equipment.entry.id === configEntry.entry.tags?.equipment
        )
      ),
    [activeEquipmentConfigurationEntries, equipmentEntries]
  )

  const assemblyConfigIds = useMemo(() => {
    if (!relevantEquipmentConfigEntries) return []
    const assemblyConfigIds = relevantEquipmentConfigEntries.flatMap(
      (entry) => entry.entry.data.assembly_configuration_ids
    )

    return assemblyConfigIds
  }, [relevantEquipmentConfigEntries])

  const { data: assemblyConfigEntries } = useGetRolodexEntriesByLoadOptions<
    AssemblyConfigurationEntry[]
  >({
    type: [RolodexEntryType.ASSEMBLY_CONFIGURATION],
    ids: assemblyConfigIds,
    limit: 10000,
  })

  const assemblyIds = useMemo(() => {
    return (
      assemblyConfigEntries
        ?.map((entry) => entry.entry.tags?.assembly)
        .filter((id) => id !== null && id !== undefined) ?? []
    )
  }, [assemblyConfigEntries])

  const { data: assemblyEntriesForEquipment } =
    useGetRolodexEntriesByLoadOptions<AssemblyEntry[]>({
      type: [RolodexEntryType.ASSEMBLY],
      ids: assemblyIds,
      limit: 10000,
    })

  const subAssemblyIds = useMemo(() => {
    if (!assemblyConfigEntries) return []
    const subAssemblyConfigIds = assemblyConfigEntries.flatMap(
      (entry) => entry.entry.data.sub_assembly_ids
    )

    return subAssemblyConfigIds.length > 0 ? subAssemblyConfigIds : ['none']
  }, [assemblyConfigEntries])

  const { data: subAssemblyEntriesForEquipment } =
    useGetRolodexEntriesByLoadOptions<SubAssemblyEntry[]>({
      type: [RolodexEntryType.SUB_ASSEMBLY],
      ids: subAssemblyIds,
      limit: 10000,
    })

  // Assembly tree data fetching (for assembly → sub-assembly updates)
  const relevantAssemblyConfigEntries = useMemo(
    () =>
      activeAssemblyConfigurationEntries?.filter((configEntry) =>
        assemblyEntries?.some(
          (assembly) => assembly.entry.id === configEntry.entry.tags?.assembly
        )
      ),
    [activeAssemblyConfigurationEntries, assemblyEntries]
  )

  const subAssemblyIdsForAssemblies = useMemo(() => {
    if (!relevantAssemblyConfigEntries) return []
    const subAssemblyIds = relevantAssemblyConfigEntries.flatMap(
      (entry) => entry.entry.data.sub_assembly_ids
    )

    return subAssemblyIds.length > 0 ? subAssemblyIds : ['none']
  }, [relevantAssemblyConfigEntries])

  const { data: subAssemblyEntriesForAssemblies } =
    useGetRolodexEntriesByLoadOptions<SubAssemblyEntry[]>({
      type: [RolodexEntryType.SUB_ASSEMBLY],
      ids: subAssemblyIdsForAssemblies,
      limit: 10000,
    })

  // Fetch parent equipment entries for assembly sync operations
  const parentEquipmentIds = useMemo(() => {
    if (!activeEquipmentConfigurationEntries) return []
    return activeEquipmentConfigurationEntries
      .map((configurationEntry) => configurationEntry.entry.tags?.equipment)
      .filter(
        (equipmentId): equipmentId is string =>
          equipmentId !== null && equipmentId !== undefined
      )
  }, [activeEquipmentConfigurationEntries])

  const { data: parentEquipmentEntries } = useGetRolodexEntriesByLoadOptions<
    EquipmentEntry[]
  >({
    type: [RolodexEntryType.EQUIPMENT],
    ids: parentEquipmentIds,
    limit: 10000,
  })

  /**
   * Syncs child entries to their parent's values for PARENT_SYNC_FIELDS.
   * Filters out sync fields from user updates and uses parent values instead.
   *
   * @param parentEntry - The parent entry (equipment or assembly)
   * @param childEntriesToSync - Array of child entries to sync
   * @param fieldsToUpdate - The fields being updated (sync fields will be replaced with parent values)
   * @returns Map of child entry ID -> corrected fields with parent sync values applied
   */
  const syncChildrenToParent = useCallback(
    <TChild extends AssemblyEntry | SubAssemblyEntry>(
      parentEntry: EquipmentEntry | AssemblyEntry | undefined,
      childEntriesToSync: TChild[],
      fieldsToUpdate: Partial<GeckoAssemblyJSON | GeckoSubAssemblyJSON>
    ): Map<string, Partial<GeckoAssemblyJSON | GeckoSubAssemblyJSON>> => {
      const correctedFieldsMap = new Map<
        string,
        Partial<GeckoAssemblyJSON | GeckoSubAssemblyJSON>
      >()

      // If no parent, just return the fields as-is for each child
      if (!parentEntry?.entry.data) {
        childEntriesToSync.forEach((childEntry) => {
          correctedFieldsMap.set(childEntry.entry.id, fieldsToUpdate)
        })
        return correctedFieldsMap
      }

      const parentData = parentEntry.entry.data

      // Extract non-sync fields from user updates
      const nonSyncFields: Partial<GeckoAssemblyJSON | GeckoSubAssemblyJSON> =
        {}
      Object.entries(fieldsToUpdate).forEach(([key, value]) => {
        if (!isParentSyncField(key)) {
          ;(nonSyncFields as Record<string, unknown>)[key] = value
        }
      })

      // Get parent's sync field values
      const parentSyncFields: Partial<
        GeckoAssemblyJSON | GeckoSubAssemblyJSON
      > = {}
      PARENT_SYNC_FIELDS.forEach((syncFieldKey) => {
        const parentValue = parentData[syncFieldKey as keyof typeof parentData]
        if (parentValue !== null && parentValue !== undefined) {
          ;(parentSyncFields as Record<ParentSyncFieldKey, unknown>)[
            syncFieldKey
          ] = parentValue
        }
      })

      // Combine: non-sync fields from user + sync fields from parent
      const correctedFields = {
        ...nonSyncFields,
        ...parentSyncFields,
      } as Partial<GeckoAssemblyJSON | GeckoSubAssemblyJSON>

      // Apply the same corrected fields to all children
      childEntriesToSync.forEach((childEntry) => {
        correctedFieldsMap.set(childEntry.entry.id, correctedFields)
      })

      return correctedFieldsMap
    },
    []
  )

  const syncEachChildWithParent = useCallback(
    <TChild extends AssemblyEntry | SubAssemblyEntry>(
      childEntries: TChild[],
      findParentForChild: (
        child: TChild
      ) => EquipmentEntry | AssemblyEntry | undefined,
      fieldsToUpdate: Partial<GeckoAssemblyJSON | GeckoSubAssemblyJSON>
    ): Map<string, Partial<GeckoAssemblyJSON | GeckoSubAssemblyJSON>> => {
      const correctedFieldsMap = new Map<
        string,
        Partial<GeckoAssemblyJSON | GeckoSubAssemblyJSON>
      >()

      childEntries.forEach((childEntry) => {
        const parentEntry = findParentForChild(childEntry)
        const singleChildCorrectedFields = syncChildrenToParent(
          parentEntry,
          [childEntry],
          fieldsToUpdate
        )
        const correctedFields =
          singleChildCorrectedFields.get(childEntry.entry.id) || fieldsToUpdate
        correctedFieldsMap.set(childEntry.entry.id, correctedFields)
      })

      return correctedFieldsMap
    },
    [syncChildrenToParent]
  )

  const updateEquipmentTree = useCallback(
    async (
      equipmentEntriesToUpdate: EquipmentEntry[],
      fieldsToUpdate: Partial<
        GeckoEquipmentJSON | GeckoAssemblyJSON | GeckoSubAssemblyJSON
      >,
      jobEntry?: JobEntry
    ) => {
      const equipmentPutEntries = equipmentEntriesToUpdate.map((entry) =>
        createEquipmentPutEntry(entry.entry.id, {
          ...entry.entry.data,
          ...(fieldsToUpdate as Partial<GeckoEquipmentJSON>),
        })
      )
      const assemblyPutEntries = assemblyEntriesForEquipment?.map((entry) =>
        createAssemblyPutEntry(entry.entry.id, {
          ...entry.entry.data,
          ...(fieldsToUpdate as Partial<GeckoAssemblyJSON>),
        })
      )

      const subAssemblyPutEntries = subAssemblyEntriesForEquipment?.map(
        (entry) =>
          entry.entry.data &&
          entry.entry.type === InventoryEntryType.SubAssembly
            ? createSubAssemblyPutEntry(entry.entry.id, {
                ...entry.entry.data,
                ...(fieldsToUpdate as Partial<GeckoSubAssemblyJSON>),
              })
            : undefined
      )

      const jobPutEntry =
        jobEntry?.entry.data && relevantEquipmentConfigEntries
          ? createJobPutEntry(
              jobEntry.entry.id,
              {
                ...jobEntry.entry.data,
                assigned_equipment_configurations: [
                  ...(jobEntry.entry.data.assigned_equipment_configurations ??
                    []),
                  ...relevantEquipmentConfigEntries.map(
                    (entry) => entry.entry.id
                  ),
                ],
              },
              jobEntry.entry.tags ?? {}
            )
          : undefined

      const putEntries = [
        ...(equipmentPutEntries || []),
        ...(assemblyPutEntries || []),
        ...(subAssemblyPutEntries || []),
        ...(jobPutEntry ? [jobPutEntry] : []),
      ].filter((entry): entry is RolodexEntryPutJson => entry !== undefined)

      try {
        try {
          await rolodexPut(putEntries)
          successNotification('Successfully updated equipment tree entries!')
        } catch {
          return errorNotification('Failed to save data to Rolodex!')
        }
      } finally {
        // refresh all of the data
        refreshEquipmentTable?.()
        refreshAssemblyTable?.()
        refreshEquipmentData?.()
        refreshAssemblyData?.()
        refreshAuditLog?.()
      }
    },
    [
      assemblyEntriesForEquipment,
      subAssemblyEntriesForEquipment,
      relevantEquipmentConfigEntries,
      rolodexPut,
      successNotification,
      errorNotification,
      refreshEquipmentTable,
      refreshEquipmentData,
      refreshAssemblyTable,
      refreshAssemblyData,
      refreshAuditLog,
    ]
  )

  const updateAssemblyTree = useCallback(
    async (
      assemblyEntriesToUpdate: AssemblyEntry[],
      fieldsToUpdate: Partial<GeckoAssemblyJSON | GeckoSubAssemblyJSON>
    ) => {
      // Sync assemblies to their parent equipment values (each assembly might have different parent)
      const assemblyCorrectedFieldsMap = syncEachChildWithParent(
        assemblyEntriesToUpdate,
        (assemblyEntry) => {
          // Find this assembly's parent equipment
          const assemblyConfigurationEntry =
            activeAssemblyConfigurationEntries?.find(
              (configurationEntry) =>
                configurationEntry.entry.tags?.assembly ===
                assemblyEntry.entry.id
            )
          const parentEquipmentConfigurationEntry = assemblyConfigurationEntry
            ? activeEquipmentConfigurationEntries?.find(
                (equipmentConfigurationEntry) =>
                  equipmentConfigurationEntry.entry.data.assembly_configuration_ids?.includes(
                    assemblyConfigurationEntry.entry.id
                  )
              )
            : undefined
          return parentEquipmentEntries?.find(
            (equipmentEntry) =>
              equipmentEntry.entry.id ===
              parentEquipmentConfigurationEntry?.entry.tags?.equipment
          )
        },
        fieldsToUpdate
      )

      const assemblyPutEntries = assemblyEntriesToUpdate.map((entry) => {
        const correctedFieldsForThisAssembly =
          assemblyCorrectedFieldsMap.get(entry.entry.id) || fieldsToUpdate
        return createAssemblyPutEntry(entry.entry.id, {
          ...entry.entry.data,
          ...(correctedFieldsForThisAssembly as Partial<GeckoAssemblyJSON>),
        })
      })

      // Sync sub-assemblies to their parent assemblies
      // For simplicity, use first assembly as parent if updating a single assembly
      const parentAssemblyForSubAssemblies =
        assemblyEntriesToUpdate.length === 1
          ? assemblyEntriesToUpdate[0]
          : undefined
      const subAssemblyCorrectedFieldsMap = syncChildrenToParent(
        parentAssemblyForSubAssemblies,
        subAssemblyEntriesForAssemblies || [],
        fieldsToUpdate
      )

      const subAssemblyPutEntries = subAssemblyEntriesForAssemblies?.map(
        (entry) =>
          entry.entry.data &&
          entry.entry.type === InventoryEntryType.SubAssembly
            ? createSubAssemblyPutEntry(entry.entry.id, {
                ...entry.entry.data,
                ...(subAssemblyCorrectedFieldsMap.get(entry.entry.id) ||
                  fieldsToUpdate),
              } as GeckoSubAssemblyJSON)
            : undefined
      )

      const putEntries = [
        ...(assemblyPutEntries || []),
        ...(subAssemblyPutEntries || []),
      ].filter((entry): entry is RolodexEntryPutJson => entry !== undefined)

      try {
        try {
          await rolodexPut(putEntries)
          successNotification('Successfully updated assembly tree entries!')
        } catch {
          return errorNotification('Failed to save data to Rolodex!')
        }
      } finally {
        // refresh all of the data
        refreshAssemblyTable?.()
        refreshSubAssemblyTable?.()
        refreshAssemblyData?.()
        refreshSubAssemblyData?.()
        refreshAuditLog?.()
      }
    },
    [
      syncEachChildWithParent,
      activeAssemblyConfigurationEntries,
      activeEquipmentConfigurationEntries,
      parentEquipmentEntries,
      syncChildrenToParent,
      subAssemblyEntriesForAssemblies,
      rolodexPut,
      successNotification,
      errorNotification,
      refreshAssemblyTable,
      refreshSubAssemblyTable,
      refreshAssemblyData,
      refreshSubAssemblyData,
      refreshAuditLog,
    ]
  )

  const updateSubAssemblyTree = useCallback(
    async (
      subAssemblyEntriesToUpdate: SubAssemblyEntry[],
      fieldsToUpdate: Partial<GeckoSubAssemblyJSON>
    ) => {
      // Sync sub-assemblies to their parent assembly values (each sub-assembly might have different parent)
      const subAssemblyCorrectedFieldsMap = syncEachChildWithParent(
        subAssemblyEntriesToUpdate,
        (subAssemblyEntry) => {
          // Find this sub-assembly's parent assembly
          const parentAssemblyConfigurationEntry =
            activeAssemblyConfigurationEntries?.find((config) =>
              config.entry.data.sub_assembly_ids.includes(
                subAssemblyEntry.entry.id
              )
            )
          const parentAssemblyId =
            parentAssemblyConfigurationEntry?.entry?.tags?.assembly
          return assemblyEntries?.find(
            (assemblyEntry) => assemblyEntry.entry.id === parentAssemblyId
          )
        },
        fieldsToUpdate
      )

      const subAssemblyPutEntries = subAssemblyEntriesToUpdate.map((entry) => {
        const correctedFieldsForThisSubAssembly =
          subAssemblyCorrectedFieldsMap.get(entry.entry.id) || fieldsToUpdate
        return createSubAssemblyPutEntry(entry.entry.id, {
          ...entry.entry.data,
          ...correctedFieldsForThisSubAssembly,
        } as GeckoSubAssemblyJSON)
      })

      const putEntries = subAssemblyPutEntries.filter(
        (entry): entry is RolodexEntryPutJson => entry !== undefined
      )

      try {
        try {
          await rolodexPut(putEntries)
          successNotification('Successfully updated sub-assembly entries!')
        } catch {
          return errorNotification('Failed to save data to Rolodex!')
        }
      } finally {
        // refresh all of the data
        refreshSubAssemblyTable?.()
        refreshSubAssemblyData?.()
        refreshAuditLog?.()
      }
    },
    [
      syncEachChildWithParent,
      activeAssemblyConfigurationEntries,
      assemblyEntries,
      rolodexPut,
      successNotification,
      errorNotification,
      refreshSubAssemblyTable,
      refreshSubAssemblyData,
      refreshAuditLog,
    ]
  )

  return {
    updateEquipmentTree,
    updateAssemblyTree,
    updateSubAssemblyTree,
    syncChildrenToParent,
    assemblyEntriesForEquipment,
    subAssemblyEntriesForEquipment,
    subAssemblyEntriesForAssemblies,
  }
}
